import en from './en';
import es from './es';

/**
 * Language resolution and lookup, with no React and no router import.
 *
 * Kept separate from index.js so this logic can be tested directly: the
 * provider has to import react-router-dom, which does not resolve under the
 * project's Jest setup without a module mock.
 *
 * Contract:
 *   - Dictionaries are flat maps of dotted keys to strings. No nesting, so a
 *     missing key is a one-line fix and never a crash.
 *   - A key absent from the active dictionary falls back to English, and a key
 *     absent from English renders the key itself. A page is therefore never
 *     blank because a translation is late.
 *   - Interpolation is {name} placeholders only. No plural rules, no dates:
 *     anything that needs formatting is passed in already formatted, so the
 *     dictionaries stay reviewable by a non-developer.
 */

export const SUPPORTED_LANGUAGES = ['en', 'es'];
export const DEFAULT_LANGUAGE = 'en';
export const STORAGE_KEY = 'finansee.lang';

const DICTIONARIES = { en, es };

// Routes that exist for one market and therefore have their own default.
// /demo was built for Spanish independent advisers, so it opens in Spanish
// unless the visitor asked for another language.
export const ROUTE_DEFAULT_LANGUAGE = { '/demo': 'es' };

export const isSupported = (value) => SUPPORTED_LANGUAGES.includes(value);

// "/demo" and "/demo/" are the same route to the router but not to a plain
// object lookup, and a link that happens to carry the slash would otherwise
// open the Spanish demo in English.
const normalizePath = (pathname) => (
    pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
);

/**
 * Resolution order, most explicit first:
 *   1. ?lang= in the URL (and it is remembered)
 *   2. a language the visitor chose earlier
 *   3. the default of this route, if it has one
 *   4. the browser language
 *   5. English
 */
export const resolveLanguage = ({ search = '', pathname = '', stored = null, browser = null } = {}) => {
    const requested = new URLSearchParams(search).get('lang');
    if (isSupported(requested)) return requested;
    if (isSupported(stored)) return stored;

    const routeDefault = ROUTE_DEFAULT_LANGUAGE[normalizePath(pathname)];
    if (isSupported(routeDefault)) return routeDefault;

    if (isSupported(browser)) return browser;
    return DEFAULT_LANGUAGE;
};

export const translate = (language, key, vars) => {
    const dictionary = DICTIONARIES[language] || DICTIONARIES[DEFAULT_LANGUAGE];
    const raw = dictionary[key]
        ?? DICTIONARIES[DEFAULT_LANGUAGE][key]
        ?? key;

    if (!vars) return raw;
    return Object.entries(vars).reduce(
        (text, [name, value]) => text.split(`{${name}}`).join(String(value)),
        raw
    );
};
